import { useEffect, useState } from 'react';
import { fetchAssignments } from '../api';
import { SENIORITY_LABELS, WORK_MODE_LABELS, formatDate, formatNumber } from '../lib/format';
import type { AssignmentListItem, AssignmentPage, Filters, Meta } from '../types';

interface AssignmentsViewProps {
  filters: Filters;
  meta: Meta | undefined;
  onFilterChange: (patch: Partial<Filters>) => void;
}

const PAGE_SIZE = 50;

export function AssignmentsView({ filters, meta, onFilterChange }: AssignmentsViewProps) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AssignmentPage | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  // Ett ändrat filter ska alltid ta oss till sida ett - annars kan man landa på
  // en tom sida 7 av ett urval som bara har två sidor.
  useEffect(() => setPage(1), [filters]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchAssignments(filters, page, PAGE_SIZE, controller.signal)
      .then((result) => {
        setData(result);
        setError(undefined);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [filters, page]);

  const sourceName = (id: string) => meta?.sources.find((s) => s.id === id)?.name ?? id;
  const areaName = (id: string) => meta?.areas.find((a) => a.id === id)?.name ?? id;
  /** Huvudområdet först, därefter de övriga uppdraget nämner. */
  const areasFor = (item: AssignmentListItem) => [item.area, ...(item.areas ?? []).filter((id) => id !== item.area)];
  const otherSources = (item: AssignmentListItem) => (item.duplicateSources ?? []).filter((id) => id !== item.source);
  const isExpired = (item: AssignmentListItem) =>
    Boolean(item.deadlineAt && item.deadlineAt.slice(0, 10) < new Date().toISOString().slice(0, 10));

  if (error) {
    return <p className="empty">Kunde inte hämta uppdrag: {error}</p>;
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <section className="panel">
      <div className="panel__header">
        <h2 className="panel__title">Uppdrag i urvalet</h2>
        <span className="panel__tools">{data ? `${formatNumber(data.total)} träffar` : 'Hämtar…'}</span>
      </div>
      <p className="panel__subtitle">
        Titeln länkar till annonsen hos källan.{' '}
        {filters.showDuplicates
          ? 'Uppdrag som flera mäklare annonserar visas en gång per mäklare.'
          : 'Uppdrag som flera mäklare annonserar visas som en rad.'}
      </p>

      {loading && !data ? (
        <div className="skeleton" style={{ height: 320 }} />
      ) : data && data.items.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Publicerat</th>
                  <th style={{ width: 96 }}>Svarsdag</th>
                  <th>Uppdrag</th>
                  <th style={{ width: 150 }}>Område</th>
                  <th style={{ width: 230 }}>Tekniker</th>
                  <th style={{ width: 130 }}>Ort</th>
                  <th style={{ width: 120 }}>Källa</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {formatDate(item.publishedAt ?? item.firstSeen)}
                      {item.publishedAt ? null : (
                        <span title="Källan anger inget publiceringsdatum – datumet är när uppdraget först sågs">
                          {' '}
                          *
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {item.deadlineAt ? formatDate(item.deadlineAt) : '–'}
                    </td>
                    <td>
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {item.title}
                      </a>
                      {item.removedAt ? (
                        <span
                          className="tag tag--removed"
                          title={`Annonsen svarade inte längre hos källan ${formatDate(item.removedAt)}.`}
                        >
                          Annons borttagen
                        </span>
                      ) : null}
                      {isExpired(item) ? (
                        <span className="tag tag--expired" title="Sista svarsdag hos källan har passerat.">
                          Svarsdag passerad
                        </span>
                      ) : null}
                      {otherSources(item).length > 0 ? (
                        <span
                          className="tag tag--dup"
                          title={`Samma uppdrag annonseras även av ${otherSources(item).map(sourceName).join(', ')}.`}
                        >
                          {`Finns hos ${(item.duplicateSources?.length ?? 1)} källor`}
                        </span>
                      ) : null}
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                        {[
                          item.client,
                          item.seniority ? SENIORITY_LABELS[item.seniority] : undefined,
                          item.workMode && item.workMode !== 'unknown' ? WORK_MODE_LABELS[item.workMode] : undefined,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td>
                      {/* Ett uppdrag kan höra till flera områden, och filtret matchar alla.
                          Visas bara huvudområdet ser en träff på ett av de andra ut som ett fel. */}
                      {areasFor(item).map((areaId, index) => (
                        <button
                          key={areaId}
                          type="button"
                          className="link-button"
                          style={{
                            padding: 0,
                            display: 'block',
                            width: '100%',
                            lineHeight: 1.35,
                            fontWeight: index === 0 ? 500 : 400,
                            color: index === 0 ? undefined : 'var(--text-muted)',
                          }}
                          onClick={() => onFilterChange({ area: areaId })}
                        >
                          {areaName(areaId)}
                        </button>
                      ))}
                    </td>
                    <td>
                      {item.technologies.slice(0, 6).map((tech) => (
                        <button
                          key={tech}
                          type="button"
                          className="tag tag--tech"
                          onClick={() =>
                            onFilterChange({
                              tech: filters.tech.includes(tech)
                                ? filters.tech.filter((t) => t !== tech)
                                : [...filters.tech, tech],
                            })
                          }
                        >
                          {tech}
                        </button>
                      ))}
                      {item.technologies.length > 6 ? (
                        <span className="tag">+{item.technologies.length - 6}</span>
                      ) : null}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{item.location ?? '–'}</td>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        style={{ padding: 0, fontWeight: 500 }}
                        onClick={() => onFilterChange({ source: item.source })}
                      >
                        {sourceName(item.source)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span>
              Sida {data.page} av {totalPages}
            </span>
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Föregående
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Nästa
            </button>
          </div>
        </>
      ) : (
        <p className="empty">Inga uppdrag matchar filtret.</p>
      )}
    </section>
  );
}
